<script setup>
/* global axios */
// Gelen kutusu ayarlarindan AI asistan baglama.
//
// NEDEN buraya da koyuyoruz: Chatwoot'ta bag yalnizca ters yonden
// kuruluyor (Captain > asistan > gelen kutulari). Kullanici bir kutunun
// ayarlarindayken "bu kutuya asistan bagla" demek istiyor ve baska bir
// menuye gidip asistani bulmak zorunda kalmamali. Ayni bagi iki yerden
// kurabiliyor; kayit tek (CaptainInbox).
//
// NEDEN "bot yapilandirmasi" sekmesinde: o sekme zaten "bu kutuya hangi
// otomatik yanitlayici bakiyor" sorusunu cevapliyor. Asistan ve webhook
// botu yan yana durunca ikisinin ayri seyler oldugu da gorunuyor.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

// Asistan olusturma dugmesi Captain'in asistan degistirme panelinin
// ICINDE sakli: kullanici asistan adina tiklamadan bulamiyor. "Hangi
// asistan" sorusu ilk kez BURADA soruldugu icin ikinci bir asistan
// olusturma yolu da burada olmali.
const yeniAsistan = () => {
  router.push({
    name: 'captain_assistants_create_index',
    params: { accountId: accountId.value },
  });
};

const accountId = computed(() => route.params.accountId);
const taban = computed(() => `/api/v1/accounts/${accountId.value}/captain`);

const asistanlar = ref([]);
const bagliAsistanId = ref('');
const secili = ref('');
const yukleniyor = ref(true);
const islemde = ref(false);
const hata = ref('');

const bagli = computed(() => Boolean(bagliAsistanId.value));

const bagliAsistan = computed(() =>
  asistanlar.value.find(a => String(a.id) === String(bagliAsistanId.value))
);

const yukle = async () => {
  yukleniyor.value = true;
  hata.value = '';
  try {
    const { data } = await axios.get(`${taban.value}/assistants`);
    const liste = data?.payload || data || [];
    asistanlar.value = Array.isArray(liste) ? liste : [];

    // Hangi asistan bu kutuya bagli? Her asistanin kutu listesine bakiyoruz;
    // Chatwoot tersten bir uc sunmuyor (kutudan asistana).
    bagliAsistanId.value = '';
    for (const a of asistanlar.value) {
      // eslint-disable-next-line no-await-in-loop
      const { data: kutular } = await axios.get(
        `${taban.value}/assistants/${a.id}/inboxes`
      );
      const dizi = kutular?.payload || kutular || [];
      if (dizi.some(k => String(k.id) === String(props.inbox.id))) {
        bagliAsistanId.value = String(a.id);
        break;
      }
    }
    secili.value = bagliAsistanId.value;
  } catch (e) {
    hata.value = e?.response?.data?.error || t('OMNUP_ASISTAN.HATA');
  } finally {
    yukleniyor.value = false;
  }
};

const bagla = async () => {
  if (!secili.value) return;

  islemde.value = true;
  hata.value = '';
  try {
    // Once varsa eski bagi kaldiriyoruz: bir kutuya iki asistan
    // baglanamaz, ikincisini eklemek sessizce catisirdi.
    if (bagli.value && bagliAsistanId.value !== secili.value) {
      await axios.delete(
        `${taban.value}/assistants/${bagliAsistanId.value}/inboxes/${props.inbox.id}`
      );
    }
    await axios.post(`${taban.value}/assistants/${secili.value}/inboxes`, {
      inbox_id: props.inbox.id,
    });
    bagliAsistanId.value = secili.value;
    useAlert(t('OMNUP_ASISTAN.BAGLANDI'));
  } catch (e) {
    hata.value = e?.response?.data?.error || t('OMNUP_ASISTAN.HATA');
  } finally {
    islemde.value = false;
  }
};

const kaldir = async () => {
  if (!bagli.value) return;
  if (!window.confirm(t('OMNUP_ASISTAN.KALDIR_ONAY'))) return;

  islemde.value = true;
  try {
    await axios.delete(
      `${taban.value}/assistants/${bagliAsistanId.value}/inboxes/${props.inbox.id}`
    );
    bagliAsistanId.value = '';
    secili.value = '';
    useAlert(t('OMNUP_ASISTAN.KALDIRILDI'));
  } catch (e) {
    hata.value = e?.response?.data?.error || t('OMNUP_ASISTAN.HATA');
  } finally {
    islemde.value = false;
  }
};

onMounted(yukle);
</script>

<template>
  <div class="flex flex-col gap-3 pb-6 mb-6 border-b border-n-weak">
    <div class="flex flex-col gap-1">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('OMNUP_ASISTAN.BASLIK') }}
      </h3>
      <p class="text-xs text-n-slate-11">{{ t('OMNUP_ASISTAN.ACIKLAMA') }}</p>
    </div>

    <div v-if="yukleniyor" class="flex justify-center p-4">
      <Spinner class="text-n-brand" />
    </div>

    <template v-else>
      <p v-if="hata" class="text-sm text-n-ruby-11">{{ hata }}</p>

      <template v-if="!asistanlar.length">
        <p class="px-3 py-2 text-xs rounded-lg bg-n-alpha-1 text-n-slate-11">
          {{ t('OMNUP_ASISTAN.ASISTAN_YOK') }}
        </p>
        <div>
          <Button
            size="sm"
            variant="solid"
            icon="i-lucide-plus"
            :label="t('OMNUP_ASISTAN.YENI')"
            @click="yeniAsistan"
          />
        </div>
      </template>

      <template v-else>
        <div class="flex flex-wrap items-center gap-2">
          <select v-model="secili" class="!mb-0 !w-auto min-w-64 text-sm">
            <option value="">{{ t('OMNUP_ASISTAN.SECINIZ') }}</option>
            <option v-for="a in asistanlar" :key="a.id" :value="String(a.id)">
              {{ a.name }}
            </option>
          </select>

          <Button
            size="sm"
            variant="solid"
            :label="t('OMNUP_ASISTAN.BAGLA')"
            :is-loading="islemde"
            :disabled="islemde || !secili || secili === bagliAsistanId"
            @click="bagla"
          />
          <Button
            v-if="bagli"
            size="sm"
            variant="ghost"
            color="ruby"
            :label="t('OMNUP_ASISTAN.KALDIR')"
            :disabled="islemde"
            @click="kaldir"
          />
          <Button
            size="sm"
            variant="ghost"
            icon="i-lucide-plus"
            :label="t('OMNUP_ASISTAN.YENI')"
            @click="yeniAsistan"
          />
        </div>

        <p class="text-xs text-n-slate-10">
          {{ t('OMNUP_ASISTAN.COKLU_NOT') }}
        </p>

        <p v-if="bagli" class="text-xs text-n-teal-11">
          {{ t('OMNUP_ASISTAN.BAGLI', { ad: bagliAsistan?.name || '—' }) }}
        </p>
        <p v-else class="text-xs text-n-slate-11">
          {{ t('OMNUP_ASISTAN.BAGLI_DEGIL') }}
        </p>
      </template>
    </template>
  </div>
</template>
